import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword, validatePasswordMatch } from '../../utils/validation';
import { useTranslation } from 'react-i18next';

function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirmPassword } = useAuth();
  
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    verificationCode: searchParams.get('code') || '',
    newPassword: '',
    confirmNewPassword: ''
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!formData.email) {
      navigate('/login');
    }
  }, [formData.email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.verificationCode || !formData.newPassword || !formData.confirmNewPassword) {
      setError('Por favor, preencha todos os campos.');
      setLoading(false);
      return;
    }

    if (!validatePasswordMatch(formData.newPassword, formData.confirmNewPassword)) {
      setError('As senhas não coincidem.');
      setLoading(false);
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError(`Senha inválida: ${passwordValidation.errors.join(', ')}`);
      setLoading(false);
      return;
    }

    try {
      const result = await confirmPassword(
        formData.email, 
        formData.verificationCode, 
        formData.newPassword
      );
      
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Erro ao alterar senha.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('login.title')}
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" gutterBottom>
            {t('login.resetPassword')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
              <br />
              <Typography variant="caption">
                Redirecionando para login em 3 segundos...
              </Typography>
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('login.email')}
              name="email"
              value={formData.email}
              disabled
              sx={{ mb: 2 }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('login.verificationCode')}
              name="verificationCode"
              value={formData.verificationCode}
              onChange={handleChange}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('login.newPassword')}
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              disabled={loading}
              helperText={t('login.passwordRequirements')}
            />
            
            {formData.newPassword && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  {t('login.passwordRequirementsTitle')}
                </Typography>
                <List dense>
                  {validatePassword(formData.newPassword).errors.map((error, index) => (
                    <ListItem key={index} sx={{ py: 0, px: 1 }}>
                      <ListItemText 
                        primary={error} 
                        primaryTypographyProps={{ 
                          variant: 'caption', 
                          color: 'error' 
                        }} 
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('login.confirmPassword')}
              name="confirmNewPassword"
              type="password"
              value={formData.confirmNewPassword}
              onChange={handleChange}
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {loading ? t('login.changing') : t('login.changePassword')}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                {t('login.backToLogin')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default ResetPassword;